import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from '@/lib/icons';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';

import { CreateCustomer } from '@/features/customers/customer';
import { createCustomer } from '@/features/customers/api';
import { useZaadKycLookup } from '@/features/customers/useZaadKycLookup';
import {
  ZaadKycBanner,
  ZaadFilledBadge,
} from '@/features/customers/ZaadKycBanner';
import {
  useRegistrationBranches,
  useSoleBranchDefault,
} from '@/features/branches/registration-branches';
import { ApiError } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { parsePhone } from '@/lib/phone';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ButtonSpinner } from '@/components/feedback';
import { TERMS_VERSION } from '@/routes/terms';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  SOMALILAND_CITIES_BY_REGION,
  regionOfCity,
} from '@/features/customers/somalilandCities';
import { DatePickerInput } from '@/components/ui/date-picker-input';

export const Route = createFileRoute('/_layout/customers/new')({
  component: NewCustomerPage,
});

const createCustomerFormSchema = (t: (key: string) => string) =>
  CreateCustomer.extend({
    firstName: z
      .string()
      .min(1, t('customers.form.validation.firstNameRequired')),
    lastName: z
      .string()
      .min(1, t('customers.form.validation.lastNameRequired')),
    // A length check no longer says anything useful now that the field emits a
    // partial `+252…` while it is being typed — `+252612345` is ten characters
    // and still not a number. Validate what actually matters: does it parse.
    phoneNumber: z
      .string()
      .min(1, t('customers.form.validation.phoneRequired'))
      .refine((v) => parsePhone(v).isValid, {
        message: t('customers.form.validation.phoneInvalid'),
      }),
    // Required here and on the server: a customer an operator registers with
    // no branch is invisible to every branch-catchment report afterwards, and
    // nothing later in the lifecycle fills it in.
    branchId: z.string().min(1, t('customers.form.validation.branchRequired')),
    // Required tick on the customer registration form — backend rejects
    // POST /v1/customers/ without a matching ``consent`` block. ``literal(true)``
    // means an unticked checkbox surfaces a Zod error on submit instead of
    // letting an unfocussed agent skip the step.
    consentAccepted: z.literal(true, {
      errorMap: () => ({
        message: t('customers.form.validation.consentRequired'),
      }),
    }),
  });

type CreateCustomerFormValues = z.infer<
  ReturnType<typeof createCustomerFormSchema>
>;

function NewCustomerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const schema = createCustomerFormSchema(t);

  const form = useForm<CreateCustomerFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      // City drives region, so both start defined — an undefined default would
      // make the Select uncontrolled until first touch.
      city: '',
      region: '',
      consentAccepted: false as unknown as true,
    },
  });

  // ── Branch, which the server requires on this route ──────────────────────
  // Shared with the walk-in wizard's inline register form: both reach the same
  // endpoint, so both have to apply the same branch policy.
  const { branches, hasNoBranch, soleBranchId } = useRegistrationBranches();

  // One branch means there is nothing to decide — pick it, but still render the
  // field so the agent can see which branch the customer is being booked into.
  useSoleBranchDefault(soleBranchId, form.watch('branchId'), (id) =>
    form.setValue('branchId', id, { shouldValidate: true })
  );

  // ── ZAAD KYC auto-fill ────────────────────────────────────────────────
  // As soon as the agent types a complete Somali number, pull the registered
  // identity from ZAAD and populate any *empty* fields — never overwriting
  // what the agent has already entered. `autoFilled` drives the per-field
  // "ZAAD" provenance badge.
  const phoneNumber = form.watch('phoneNumber');
  const kyc = useZaadKycLookup(phoneNumber);
  const [autoFilled, setAutoFilled] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (kyc.status !== 'found' || !kyc.data) return;
    const d = kyc.data;
    const justFilled: string[] = [];
    const fillIfEmpty = (
      name: keyof CreateCustomerFormValues,
      value: string | null | undefined
    ) => {
      if (!value) return;
      const current = form.getValues(name);
      if (current == null || current === '') {
        form.setValue(name, value as never, { shouldDirty: true });
        justFilled.push(name as string);
      }
    };
    fillIfEmpty('firstName', d.firstName);
    fillIfEmpty('lastName', d.lastName);
    if (justFilled.length) {
      setAutoFilled((prev) => new Set([...prev, ...justFilled]));
    }
  }, [kyc.status, kyc.data, form]);

  // The auto-fill above only ever fills blanks, so an agent who typed the name
  // before the number keeps their own version with no way to take ZAAD's.
  // Offer the override explicitly, and only when the two actually disagree.
  const typedFirstName = form.watch('firstName');
  const typedLastName = form.watch('lastName');
  const zaadNameDiffers =
    kyc.status === 'found' &&
    !!kyc.data &&
    !!(kyc.data.firstName ?? kyc.data.lastName) &&
    ((kyc.data.firstName ?? '').trim() !== (typedFirstName ?? '').trim() ||
      (kyc.data.lastName ?? '').trim() !== (typedLastName ?? '').trim());

  const applyZaadName = () => {
    const d = kyc.data;
    if (!d) return;
    const applied: string[] = [];
    if (d.firstName) {
      form.setValue('firstName', d.firstName, {
        shouldDirty: true,
        shouldValidate: true,
      });
      applied.push('firstName');
    }
    if (d.lastName) {
      form.setValue('lastName', d.lastName, {
        shouldDirty: true,
        shouldValidate: true,
      });
      applied.push('lastName');
    }
    if (applied.length) {
      setAutoFilled((prev) => new Set([...prev, ...applied]));
    }
  };

  const fieldLabel = (text: string, name: string) => (
    <span className="inline-flex items-center gap-2">
      {text}
      {autoFilled.has(name) && <ZaadFilledBadge />}
    </span>
  );

  const onSubmit = async (values: CreateCustomerFormValues) => {
    try {
      // Tell the backend the identity was pulled from ZAAD so it can confirm
      // and auto-verify KYC — skipping the manual branch verification step.
      const result = await createCustomer({
        ...values,
        kycSourcedFromZaad: kyc.status === 'found',
      });

      // handleApiResponse throws on backend failure, so reaching here means
      // the customer was created. Trust `succeeded` as the source of truth and
      // handle a missing `value` by falling back to the customers list — never
      // show an error toast for a successful enrolment.
      if (!result.succeeded) {
        toast({
          title: 'Failed to create customer',
          description: result.errors?.[0] || 'Please try again.',
          variant: 'destructive',
        });
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast({
        title: t('common.success'),
        description: 'Customer registered successfully',
      });

      if (result.value?.id) {
        navigate({
          to: '/customers/$customerId',
          params: { customerId: result.value.id },
        });
      } else {
        navigate({ to: '/customers' });
      }
    } catch (error) {
      const description =
        error instanceof ApiError
          ? error.errors?.[0] || error.message
          : (error as Error)?.message || 'Could not reach the server';
      toast({
        title: 'Failed to create customer',
        description,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/customers' })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">{t('customers.form.title')}</h1>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>{t('customers.detail.personalInfo')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {fieldLabel(t('customers.form.firstName'), 'firstName')}
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Mohamed" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {fieldLabel(t('customers.form.lastName'), 'lastName')}
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Ali" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t('customers.form.phoneNumber')}</FormLabel>
                    <FormControl>
                      <PhoneInput
                        name={field.name}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        invalid={!!fieldState.error}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <ZaadKycBanner
                status={kyc.status}
                data={kyc.data}
                onRefresh={() => void kyc.refetch()}
                onApplyName={zaadNameDiffers ? applyZaadName : undefined}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customers.form.email')}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="email@example.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="nationalId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('customers.form.nationalId')}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dateOfBirth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('customers.form.dateOfBirth')}
                    </FormLabel>
                    <FormControl>
                      <DatePickerInput
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        aria-label={t('customers.form.dateOfBirth')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="gender"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('customers.form.gender')}
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('customers.form.city')}
                    </FormLabel>
                    <Select
                      value={field.value ?? ''}
                      onValueChange={(value) => {
                        field.onChange(value);
                        // Region is implied by the city, so it is set here
                        // rather than typed — the two can never disagree.
                        form.setValue('region', regionOfCity(value) ?? '', {
                          shouldDirty: true,
                        });
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t(
                              'customers.form.cityPlaceholder',
                              'Select a city'
                            )}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {SOMALILAND_CITIES_BY_REGION.map(([region, cities]) => (
                          <SelectGroup key={region}>
                            <SelectLabel>{region}</SelectLabel>
                            {cities.map((c) => (
                              <SelectItem key={c.city} value={c.city}>
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="region"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('customers.form.region')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        readOnly
                        tabIndex={-1}
                        aria-readonly
                        placeholder={t(
                          'customers.form.regionDerived',
                          'Set from the selected city'
                        )}
                        className="bg-muted/50 text-muted-foreground"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('customers.form.address')}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="branchId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('customers.form.branch')}
                      <span aria-hidden className="ml-0.5 text-destructive">
                        *
                      </span>
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ''}
                      disabled={hasNoBranch}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t(
                              'customers.form.branchPlaceholder',
                              'Select a branch'
                            )}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {branches.map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.name}
                            {b.city ? ` — ${b.city}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {soleBranchId && !hasNoBranch && (
                      <p className="text-xs text-muted-foreground">
                        {t(
                          'customers.form.branchSole',
                          'Your only branch — the customer is registered here.'
                        )}
                      </p>
                    )}
                    {hasNoBranch && (
                      <p className="text-xs text-destructive">
                        {t(
                          'customers.form.branchNoneAssigned',
                          'You have no branch assigned, so you cannot register a customer. Ask an administrator to assign you one.'
                        )}
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="consentAccepted"
                render={({ field }) => (
                  <FormItem className="rounded-lg border bg-muted/30 p-4">
                    <div className="flex items-start gap-3">
                      <FormControl>
                        <Checkbox
                          id="consent-accepted"
                          checked={field.value === true}
                          onCheckedChange={(checked) =>
                            field.onChange(checked === true)
                          }
                          className="mt-0.5"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-snug">
                        <label
                          htmlFor="consent-accepted"
                          className="text-sm font-medium cursor-pointer"
                        >
                          {t('customers.form.consentAccepted')}
                        </label>
                        <p className="text-xs text-muted-foreground">
                          {t('customers.form.consentDescription')}{' '}
                          <a
                            href="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline-offset-2 hover:underline"
                          >
                            {t('customers.form.consentLink')} (v{TERMS_VERSION})
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={form.formState.isSubmitting || hasNoBranch}
                >
                  {form.formState.isSubmitting && (
                    <ButtonSpinner className="mr-2" />
                  )}
                  {t('common.actions.save')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate({ to: '/customers' })}
                >
                  {t('common.actions.cancel')}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
